/** CSS do widget, injetado no shadow root (isolado do site host). `all:initial`
    no root zera herança; usamos system-ui. As cores das SUPERFÍCIES (fundo, painel,
    texto, bordas) são tokens que trocam por tema via `.altbug[data-tema="dark"]`.

    A rampa neutra é TRUE GRAY, espelhando `web/app/styles/tokens/colors.css` (DEV-122).
    Antes era slate do Tailwind — cinza AZULADO —, e o efeito era o widget puxar para o
    azul mesmo com um relator de acento laranja: o "tema pouco neutro" da nota do DEV-111.
    O acento é DADO (vem do relator, por `setProperty` no container); o verde de sucesso e
    o vermelho de alerta são fixos nos dois temas, iguais aos `--success`/`--danger` do
    front. Cor nova aqui nasce TOKEN: literal solto não troca de tema, que foi como o
    toast acabou invisível no escuro.

    CUIDADO ao comentar aqui dentro: isto é um template literal, então **backtick fecha a
    string** e o `tsc` passa a ler CSS como TypeScript (erro TS1443, em linha que parece não
    ter nada de errado). Cite seletor e valor com aspas simples, como o resto do arquivo. */
export const CSS = `
.altbug, .altbug * { box-sizing: border-box; }
.altbug {
  all: initial;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;

  /* Tokens — tema LIGHT (default) */
  --ab-accent: #2f6df6;
  --ab-accent-hover: #245ad6;
  --ab-success: #16a34a;
  /* Alerta = o vermelho das badges de resposta nova. Um valor só nos dois temas, como o
     '--danger' do front: é aviso, e aviso não desbota no escuro. */
  --ab-alerta: #dc2626;
  --ab-overlay: rgba(27,28,31,.55);
  --ab-panel: #ffffff;
  --ab-fg: #1b1c1f;
  --ab-muted: #727479;
  --ab-border: #e4e4e6;
  --ab-input-bg: #ffffff;
  --ab-input-border: #d0d1d5;
  --ab-hover: rgba(27,28,31,.06);
  /* Superfície INVERTIDA (tooltip do pino e toast): escura no claro, e no escuro CLARA o
     bastante para não sumir contra '--ab-panel' — o texto em cima é branco nos dois. */
  --ab-inverso: #1b1c1f;
  /* Fechar do painel: translúcido, e por isso um token por tema — ele fica metade sobre o
     card, metade sobre o fundo escurecido, e o ✕ é branco nos dois. */
  --ab-fechar: rgba(27,28,31,.5);
  --ab-fechar-hover: rgba(27,28,31,.72);

  color: var(--ab-fg);
}

.altbug[data-tema="dark"] {
  --ab-overlay: rgba(15,16,17,.66);
  --ab-panel: #212121;
  --ab-fg: #f5f5f5;
  --ab-muted: #9e9e9e;
  --ab-border: rgba(255,255,255,.14);
  --ab-input-bg: #2a2a2a;
  --ab-input-border: rgba(255,255,255,.18);
  --ab-hover: rgba(255,255,255,.10);
  /* Invertido no escuro é MAIS CLARO que o painel: repetir o quase-preto do claro faria o
     tooltip e o toast desaparecerem sobre o card. */
  --ab-inverso: #474747;
  /* No escuro, escurecer mais não aparece: o translúcido tem de ser CLARO sobre o card. */
  --ab-fechar: rgba(255,255,255,.22);
  --ab-fechar-hover: rgba(255,255,255,.36);
}

/* ---- Launcher: círculo em repouso, PAINEL quando aberto ----
   Fechado, o '.fab' é só a moldura do círculo azul. No hover ele ganha a superfície do
   widget (fundo, borda, raio) e a ação principal passa a ser a linha em destaque DENTRO do
   painel, com as outras ações como linhas normais. O painel é o mesmo material do resto do
   widget — o azul fica reservado para a ação principal, em vez de pintar o menu inteiro.

   O '.fab' é o container e a '.fab-acao' é a ação principal. Essa separação é o que faz o
   painel dimensionar sozinho: as linhas são filhas EM FLUXO, então a largura do painel é a
   da linha mais larga, sem medir nada em JS. */
.fab {
  position: fixed;
  z-index: 2147483001;
  display: flex;
  flex-direction: column;
  padding: 0;
  /* Borda transparente já no repouso: sem ela o painel cresceria 2px no instante em que a
     borda aparece, e o círculo daria um pulinho. */
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: var(--ab-fg);
  cursor: grab;
  box-shadow: 0 6px 20px rgba(0,0,0,.25);
  pointer-events: auto;
  touch-action: none; /* pointermove sem o browser rolar/gesticular */
  user-select: none;
}
/* Com o menu abaixo, a ação principal é a PRIMEIRA linha: ela tem de ficar sempre no lugar
   onde o círculo estava, senão o cursor precisa viajar para reportar. */
.fab.menu-abaixo { flex-direction: column-reverse; }
/* ABERTO = (hover ou foco) E NÃO arrastando.
   Arrastar não é abrir: durante o gesto o painel fecha, para a pessoa mover o launcher e não
   um card que muda de tamanho debaixo do cursor — e para os clamps de borda medirem o
   círculo, que é o que de fato vai ficar na tela. Sem isso o ícone, que é o que a pessoa
   arrasta, não alcança os cantos do lado do rótulo.

   O ':not(.arrastando)' entra em TODAS as regras do estado aberto em vez de um bloco de
   override depois. Um override empata em especificidade com elas e só venceria pela ordem no
   arquivo — e as regras do '.fab-menu' vivem em outra seção, bem mais abaixo, então o
   override perdia calado: no meio do arraste as linhas continuavam visíveis, agora sem
   superfície nenhuma atrás (fundo transparente, raio de cápsula, sombra gigante).

   Pelo mesmo motivo, e na mesma forma, toda regra que ABRE o painel carrega TAMBÉM
   ':not(.talo)' (DEV-222): encolhido, o launcher não abre painel nenhum. Se faltar em uma
   delas, um painel de ~200px brota de uma faixa de 12px na borda. Auditoria mecânica:
   'grep not(.arrastando)' — as únicas linhas SEM ':not(.talo)' devem ser as duas do
   '~ .av-balao', lá embaixo, que apagam o balão em vez de abrir o painel (e no talo não há
   balão nenhum: quem esconde leva o aviso junto, ver 'limparAviso' em widget.ts). */
.fab:hover:not(.arrastando):not(.talo),
.fab:focus-within:not(.arrastando):not(.talo) {
  gap: 2px;
  padding: 4px;
  border-radius: 16px;
  border-color: var(--ab-border);
  background: var(--ab-panel);
}
.fab:active { cursor: grabbing; }
/* Anel de foco próprio: o padrão do browser (com 'all: initial' no root, o do UA) desenha um
   contorno de outra paleta em volta do painel inteiro. */
.fab:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 2px; }

/* A ação principal: o círculo azul em repouso, a linha azul do painel quando aberto. */
.fab-acao {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 999px;
  background: var(--ab-accent);
  color: #fff;
  font: 600 14px/1 system-ui, sans-serif;
  cursor: inherit;
  transition: background .18s ease;
}
.fab:hover:not(.arrastando):not(.talo) .fab-acao,
.fab:focus-within:not(.arrastando):not(.talo) .fab-acao { padding: 12px 14px; border-radius: 12px; }
/* Elemento próprio para a ação principal também resolve o realce: escurece só a linha sob o
   ponteiro. Enquanto o launcher inteiro era o botão, passar o mouse em "esconder" acendia a
   linha de reportar junto, como se as duas fossem o alvo. */
.fab-acao:hover { background: var(--ab-accent-hover); }
.fab-acao svg { width: 18px; height: 18px; flex: 0 0 auto; }
/* Direção da expansão: o ícone ancora no canto e o rótulo cresce para o CENTRO.
   exp-left = ícone à direita (row-reverse), rótulo cresce p/ a esquerda; exp-right = padrão. */
.fab.exp-left .fab-acao { flex-direction: row-reverse; }
/* Rótulo: colapsado (largura 0) → aparece no hover. A LARGURA não anima; a caixa nasce no
   tamanho final e o texto entra por opacidade, que não move nada. Animar a largura arrastava
   o painel por 200ms enquanto as linhas, já abertas, esperavam em cima. */
.fab-label {
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
  transition: opacity .14s ease;
}
/* Expande também no ':focus-within': pelo teclado o painel abre sem hover, e a linha
   principal ainda em círculo dentro de um painel largo nasce torta. */
.fab:hover:not(.arrastando):not(.talo) .fab-label,
.fab:focus-within:not(.arrastando):not(.talo) .fab-label { max-width: 220px; opacity: 1; }
.fab.exp-right:hover:not(.arrastando):not(.talo) .fab-label,
.fab.exp-right:focus-within:not(.arrastando):not(.talo) .fab-label { margin-left: 8px; }  /* rótulo à direita */
.fab.exp-left:hover:not(.arrastando):not(.talo) .fab-label,
.fab.exp-left:focus-within:not(.arrastando):not(.talo) .fab-label { margin-right: 8px; }  /* rótulo à esquerda */
.pos-bottom-right { right: 20px; bottom: 20px; }
.pos-bottom-left  { left: 20px;  bottom: 20px; }
.pos-top-right    { right: 20px; top: 20px; }
.pos-top-left     { left: 20px;  top: 20px; }

/* ---- TALO: o launcher RECOLHIDO, que é o repouso do CELULAR (DEV-222 / DEV-224) ----
   No dedo não há hover, e um launcher inteiro parado sobre a tela do site anfitrião é um
   estorvo — então o repouso ali é uma pílula fina COLADA na lateral, e um toque nela abre o
   mesmo menu que o mouse abre no desktop. No desktop esta classe não existe: lá o repouso é
   a bolinha, e "esconder" esconde (ver 'formaDoLauncher').

   É o MESMO '.fab', com uma classe a mais — e é isso que faz o desenho caber: posição
   arrastada, arraste, balão irmão e 'pointer-events' continuam sendo os de sempre.

   ARITMÉTICA, e ela é acoplamento silencioso: o que encolhe é só a PINTURA ('.fab-acao',
   12x42). A CAIXA continua 44x44 — 1 de borda + 30 de padding transparente + 12 de pintura
   + 1 —, que é exatamente o retângulo do launcher cheio (padding 12 + ícone 18 + padding 12,
   mais as bordas) e é o que 'aplicarPosFab' MEDE em widget.ts. Mexer no padding ou no
   tamanho do ícone da '.fab-acao' desfaz a igualdade e o launcher PULA de lugar ao abrir,
   sem erro e sem teste que acuse.

   Os 30px transparentes ficam do lado de DENTRO da tela e são alvo de dedo: a faixa é
   discreta de propósito, e 12px não é alvo. */
.fab.talo {
  /* A sombra passa a ser da pintura: no '.fab' ela desenharia um retângulo de 44px em volta
     de uma caixa invisível. */
  box-shadow: none;
  /* E o raio de cápsula do '.fab' SAI: aqui ele não pinta nada (o fundo é transparente), mas
     border-radius RECORTA o hit-test do próprio elemento — os 30px de alvo transparente
     viravam um segmento de círculo, e o dedo perdia justamente as pontas da pílula. */
  border-radius: 0;
  /* 'touch-action' NÃO volta para 'manipulation' aqui: o talo se arrasta (DEV-224), e sem o
     'none' herdado do '.fab' o browser rolaria a página no meio do gesto. O preço é a
     rolagem do site anfitrião não pegar nesses 44x44 encostados na borda — o mesmo preço
     que o launcher cheio sempre cobrou, agora numa área bem menor. */
}
/* Colado na lateral também no canto PADRÃO, antes de qualquer arraste: 'aplicarPosFab' zera
   o x de quem arrastou o talo, mas sem posição guardada quem posiciona é o 'pos-*', e os
   20px dele deixariam a pílula boiando a um dedo da borda. O eixo vertical fica como está —
   é o único que o arraste move. */
.fab.talo.pos-bottom-right, .fab.talo.pos-top-right { right: 0; }
.fab.talo.pos-bottom-left, .fab.talo.pos-top-left { left: 0; }
.fab.talo .fab-acao {
  width: 12px;
  height: 42px;
  padding: 0;
  /* Pílula inteira, e não meia-cápsula encostada na borda: o talo herda a POSIÇÃO do
     launcher, que no canto padrão fica a 20px da borda (e, arrastado, no x que a pessoa
     deixou). Uma meia-cápsula ali fica com o lado reto virado para o vazio e parece um
     recorte — conferido a olho na demo. A pílula lê como peça inteira em qualquer x, e é o
     que permite ao talo NÃO ter caminho próprio de posicionamento: quem o coloca na tela
     continua sendo o 'pos-*'/'aplicarPosFab' do launcher, sem uma linha a mais. */
  border-radius: 999px;
  /* Discreto em repouso, inteiro sob o cursor. Opacidade, e não uma cor com alfa: o acento é
     DADO (vem do relator) e não dá para derivar um rgba dele sem 'color-mix'. */
  opacity: .55;
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
  transition: opacity .18s ease, background .18s ease;
}
.fab.talo:hover .fab-acao { opacity: 1; }
.fab.talo .fab-acao svg { display: none; }
/* O anel de foco também é da PINTURA: no '.fab' ele contornaria os 44px transparentes, e a
   faixa apareceria solta dentro de um quadrado sem nada. O 'opacity: 1' junto NÃO é enfeite:
   'opacity' compõe o elemento como grupo — fundo, sombra E contorno —, então sem ele o anel
   de foco sairia a 55%, que é o oposto do que um indicador de foco tem de ser. */
.fab.talo:focus-visible { outline: none; }
.fab.talo:focus-visible .fab-acao {
  opacity: 1;
  outline: 2px solid var(--ab-accent);
  outline-offset: 2px;
}
/* 'exp-left' = ancorado à DIREITA: a pintura fica na ponta direita da caixa e os 30px de
   alvo transparente crescem para a esquerda, para DENTRO da tela. 'exp-right' é o espelho.
   Sem isso o alvo cresceria para fora da viewport e o dedo teria só os 12px da pintura. */
.fab.talo.exp-left  { padding-left: 30px; }
.fab.talo.exp-right { padding-right: 30px; }

/* Overlay (tela única de report / sucesso) — sem barra; o conteúdo fica centralizado. */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483001;
  background: var(--ab-overlay);
  display: flex;
  pointer-events: auto;
  overflow: auto;
  padding: 24px;
}
.spacer { flex: 1; }
.btn {
  border: 1px solid var(--ab-border);
  background: transparent;
  color: var(--ab-fg);
  font: 600 13px/1 system-ui, sans-serif;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.btn:hover { background: var(--ab-hover); }
.btn.primary { background: var(--ab-accent); border-color: var(--ab-accent); color: #fff; }
.btn.primary:hover { background: var(--ab-accent-hover); }
.btn:disabled { opacity: .55; cursor: default; }

/* Conteúdo em duas colunas: print (esquerda) + formulário (direita). margin:auto
   centraliza o bloco (vertical+horizontal) no overlay; align-items:stretch estica o
   card do form até a altura da print. Rola quando maior que a tela. */
.content {
  display: flex;
  gap: 16px;
  align-items: stretch;
  width: 100%;
  max-width: 1400px;
  margin: auto;
}
.pane-shot { flex: 1 1 auto; min-width: 0; position: relative; }
/* O card do formulário acompanha a altura da print (align-items:stretch no
   .content): vira coluna, rola se o conteúdo passar, e ancora as ações no rodapé. */
.pane-form {
  flex: 0 0 380px;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background: var(--ab-panel);
  border: 1px solid var(--ab-border);
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 10px 40px rgba(0,0,0,.25);
}
@media (max-width: 900px) {
  .content { flex-direction: column; align-items: stretch; }
  .pane-form { flex-basis: auto; max-width: none; width: 100%; overflow: visible; }
  .pane-shot { position: static; }
  /* Empilhado, a print manda na altura — e a print de um CELULAR é um retângulo em pé: a
     100% de largura ela sozinha passa da tela e empurra o formulário inteiro para baixo da
     dobra. Quem acabou de tirar o print via a imagem e nenhuma pergunta. Isso ficou
     visível quando o celular passou a tirar print sozinho (DEV-277); antes era raro.

     O teto vai na IMAGEM, e o wrapper encolhe junto (fit-content), porque a marca é
     desenhada em coordenada 0–1 sobre a caixa do WRAPPER: com ele mais largo que a imagem
     — que é o que um object-fit: contain produziria — todo pino sairia deslocado.
     (Sem crase neste arquivo: o CSS mora dentro de um template literal.) */
  .imgwrap { width: fit-content; max-width: 100%; margin: 0 auto; }
  .shot { width: auto; max-width: 100%; max-height: 45vh; }
}
/* Formulário SEM print (caminho secundário, DEV-144). Sem o painel da imagem ao lado, o
   card de 380px ficaria encostado numa borda de uma tela vazia — aqui ele é o conteúdo,
   então centraliza e ganha uma largura de leitura. É também a primeira tela do widget que
   roda em celular de verdade: a captura é desktop-only (getDisplayMedia), abrir chamado
   não é. */
.content.sem-shot { max-width: 520px; }
.content.sem-shot .pane-form { flex: 1 1 auto; max-width: none; width: 100%; }

/* Escolha do tipo: radios visíveis, não um select. São poucas opções e ver o conjunto é o
   que revela que existe uma escolha — um select fechado a esconde atrás de um clique. */
.tipo-ops { display: flex; flex-wrap: wrap; gap: 8px; }
.tipo-op {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid var(--ab-border);
  border-radius: 999px;
  cursor: pointer;
  font: 400 13px/1.2 system-ui, sans-serif;
  color: var(--ab-fg);
}
.tipo-op:hover { background: var(--ab-hover); }
.tipo-op input { accent-color: var(--ab-accent); margin: 0; }
.tipo-op:has(input:checked) { border-color: var(--ab-accent); background: var(--ab-hover); }

/* "Anexar uma captura": ação secundária, largura cheia, com o ícone à esquerda. Fica depois
   dos campos porque é sobre o que VAI JUNTO, não sobre o que se escreve. */
.shot-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
}
.shot-add svg { width: 16px; height: 16px; flex: none; }

/* "Anexar um arquivo" (DEV-211): mesma forma do "anexar captura" — as duas respondem à
   mesma pergunta ("o que mais ajuda a entender?") e ficam lado a lado. */
.anexo-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
}
.anexo-add svg { width: 16px; height: 16px; flex: none; }
/* O chip do arquivo escolhido. Linha, não miniatura: arquivo não tem prévia, o que
   identifica é o NOME — e o tamanho ao lado responde "é este mesmo?" antes do upload. */
.anexo-escolhido {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 8px;
  border: 1px solid var(--ab-border);
  border-radius: 8px;
  font-size: 12px;
}
.anexo-escolhido[hidden] { display: none; }
/* O nome cede espaço (é ele que corta com reticências); tamanho e ✕ não encolhem. */
.anexo-nome { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anexo-tam { flex: none; color: var(--ab-muted); }
.anexo-tirar {
  flex: none;
  width: 20px; height: 20px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--ab-fechar);
  color: #fff;
  cursor: pointer;
}
.anexo-tirar:hover { background: var(--ab-fechar-hover); }
.anexo-tirar svg { width: 11px; height: 11px; }

.imgwrap { position: relative; line-height: 0; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,.4); }
.shot { display: block; width: 100%; height: auto; user-select: none; -webkit-user-drag: none; }
.layer { position: absolute; inset: 0; }
.layersvg { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
/* NB: a classe de cursor NÃO pode ser .pin (colide com o marcador .pin e esmaga
   a imagem). Usamos .crosshair. */
.imgwrap.crosshair { cursor: crosshair; }

/* A ESPERA da print (DEV-277). Ocupa a caixa que a imagem vai ocupar — a proporção vem do
   viewport, inline no elemento — para a imagem entrar no lugar do aviso sem empurrar o
   formulário no meio da digitação. É contorno de tela, não toast: fica ONDE a imagem vai
   ficar, some quando ela chega, e não interrompe quem está escrevendo do lado. */
.gerando {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border-radius: 8px;
  background: var(--ab-panel);
  border: 1px dashed var(--ab-border);
  padding: 16px;
  text-align: center;
}
.gerando-barra {
  width: 120px;
  height: 3px;
  border-radius: 2px;
  background: var(--ab-border);
  overflow: hidden;
  position: relative;
}
/* Barra indeterminada: o redesenho não tem progresso real para relatar (a lib percorre o
   DOM inteiro), e uma porcentagem inventada é pior que nenhuma. */
.gerando-barra::after {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 40%;
  border-radius: 2px;
  background: var(--ab-accent);
  animation: ab-gerando 1.1s ease-in-out infinite;
}
@keyframes ab-gerando {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
}
.gerando-txt { font-size: 13px; font-weight: 600; color: var(--ab-fg); }
.gerando-sub { font-size: 12px; color: var(--ab-muted); max-width: 34ch; }

.pin {
  position: absolute;
  transform: translate(-50%, -50%);
  min-width: 22px; height: 22px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--ab-accent); color: #fff;
  font: 700 11px/1 system-ui, sans-serif;
  box-shadow: 0 0 0 2px #fff;
  cursor: pointer;
}

/* O balão do comentário, ancorado no pino (left/top no centro dele) e deslocado por
   transform. Não intercepta o ponteiro: o clique atravessa e chega ao pino, que é quem
   esconde/mostra — e o balão nunca fica na frente da imagem para o gesto.

   Na LUPA há um por marca comentada, todos visíveis de saída (ver 'ligarBaloesDosPinos').
   No EDITOR é um só, no hover ('ligarTooltipDosPinos'). O deslocamento sai de variáveis
   para as variantes combinarem sem virar quatro regras de transform. */
.pin-tip {
  position: absolute;
  --tx: -50%;
  --ty: calc(-100% - 14px);
  --seta: 50%;
  transform: translate(var(--tx), var(--ty));
  /* 'max-content' + teto, e não largura automática: o balão é absoluto ancorado no 'left'
     do pino, então a largura disponível é a FAIXA do pino até a borda do wrap — uma marca
     a 95% ganhava uma coluna de 5% e o comentário descia letra por letra. O transform
     reposiciona depois; quem decide o tamanho é o texto. */
  width: max-content;
  max-width: 240px;
  padding: 6px 9px;
  border-radius: 8px;
  background: var(--ab-inverso);
  color: #fff;
  font: 500 12px/1.35 system-ui, sans-serif;
  box-shadow: 0 6px 20px rgba(0,0,0,.35);
  pointer-events: none;
  white-space: pre-wrap;
  word-break: break-word;
  z-index: 3;
}
.pin-tip[hidden] { display: none; }
.pin-tip.vazio { font-style: italic; opacity: .85; }
/* Pino perto do topo: o balão desce, senão o 'overflow: hidden' do '.imgwrap' o corta
   (o recorte existe para as marcas não vazarem dos cantos arredondados, e vale para ele
   também, por z-index nenhum). Sem isto o comentário de uma marca no alto da print é
   inalcançável — e no celular ele é o ÚNICO jeito de ler. */
.pin-tip.abaixo { --ty: calc(100% + 14px); }
/* Pino junto da borda lateral: o balão deixa de ser centrado e encosta o lado de dentro,
   pelo mesmo recorte. A escolha vem da COORDENADA da marca, não de medir a caixa — não há
   o que recalcular no resize, e a lupa redimensiona junto com a janela. */
.pin-tip.esq { --tx: -16px; --seta: 16px; }
.pin-tip.dir { --tx: calc(-100% + 16px); --seta: calc(100% - 16px); }
/* Setinha apontando pro pino. */
.pin-tip::after {
  content: "";
  position: absolute;
  left: var(--seta); top: 100%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: var(--ab-inverso);
}
.pin-tip.abaixo::after {
  top: auto; bottom: 100%;
  border-top-color: transparent;
  border-bottom-color: var(--ab-inverso);
}

/* Formulário (painel direito) */
.pane-form h2 { font: 700 18px/1.2 system-ui, sans-serif; margin: 0 0 16px; color: var(--ab-fg); }
.field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.label { font: 700 11px/1 system-ui, sans-serif; letter-spacing: .04em; text-transform: uppercase; color: var(--ab-muted); }
.input, .textarea {
  font: 400 14px/1.4 system-ui, sans-serif;
  color: var(--ab-fg);
  border: 1px solid var(--ab-input-border);
  border-radius: 8px;
  padding: 8px 10px;
  width: 100%;
  background: var(--ab-input-bg);
}
.input:focus, .textarea:focus { outline: 2px solid var(--ab-accent); outline-offset: -1px; border-color: var(--ab-accent); }
.textarea { min-height: 96px; resize: vertical; }
.pins { display: flex; flex-direction: column; gap: 8px; }
.pinrow { display: flex; align-items: center; gap: 8px; }
.pinrow .input { flex: 1 1 auto; min-width: 0; }
.pinnum { flex: 0 0 auto; width: 20px; height: 20px; border-radius: 50%; background: var(--ab-accent); color: #fff; font: 700 11px/20px system-ui, sans-serif; text-align: center; }
/* Botão ✕ p/ remover a marca (ao lado do comentário). */
.iconbtn {
  flex: 0 0 auto;
  width: 28px; height: 28px;
  padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font: 400 14px/1 system-ui, sans-serif;
  color: var(--ab-muted);
}
.iconbtn:hover { background: var(--ab-hover); color: var(--ab-fg); }
.hint { font: 400 12px/1.4 system-ui, sans-serif; color: var(--ab-muted); margin: 6px 0 0; }
/* Hint DE UM CAMPO (não do formulário): colada nele, e sem o respiro que separa blocos. */
.campo-hint { margin: 2px 0 0; }
/* Erro de um campo, junto dele — não em toast. O que a pessoa precisa é ler a explicação
   com o rascunho ainda na tela; um toast some justamente enquanto ela procura o que
   corrigir. Mesma razão do erro de envio no composer do front. */
.erro-campo {
  margin: 4px 0 0;
  font: 500 12px/1.4 system-ui, sans-serif;
  color: var(--ab-alerta);
}
.erro-campo[hidden] { display: none; }
/* Erro do FORMULÁRIO: o destino do 422 cujo campo esta tela não tem (screenshot,
   dados.tipo). Existe porque o silêncio era o modo de falha antigo — o widget mandava
   todo 422 para o campo de e-mail, que nem sempre existe, e o envio falhava sem sintoma. */
.erro-form {
  margin: 10px 0 0;
  font: 500 12px/1.4 system-ui, sans-serif;
  color: var(--ab-alerta);
}
.erro-form[hidden] { display: none; }
/* Ações abaixo do formulário (Cancelar/Enviar), ancoradas à direita e no rodapé. */
.pane-form .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: auto; padding-top: 18px; }

/* Tela de sucesso (pós-envio). Segue PEQUENA de propósito — é diálogo, não tela. O '100%'
   troca o '94vw' pelo mesmo motivo do '.painel': viewport-unit não desconta o padding do
   overlay e estourava em tela estreita. */
.review { margin: auto; width: min(460px, 100%); padding: 22px; background: var(--ab-panel); border: 1px solid var(--ab-border); border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,.4); }
.review.success { text-align: center; }
.review.success h2 { font: 700 18px/1.2 system-ui, sans-serif; margin: 12px 0 8px; color: var(--ab-fg); }
.okicon { width: 56px; height: 56px; margin: 4px auto 0; border-radius: 50%; background: var(--ab-success); color: #fff; font: 700 30px/56px system-ui, sans-serif; }
.okcode { font: 400 15px/1.5 system-ui, sans-serif; color: var(--ab-fg); margin: 0 0 4px; }
.okcode strong { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.okhint { font: 400 13px/1.4 system-ui, sans-serif; color: var(--ab-muted); margin: 0 0 18px; }
.oklink { color: var(--ab-accent); font-weight: 600; text-decoration: none; }
.oklink:hover { text-decoration: underline; }
.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }
.actions.center { justify-content: center; }

/* ---- Porta de login, quando o relator exige conta (DEV-260) ---- */
.review.entrar h2 { font: 700 17px/1.3 system-ui, sans-serif; margin: 0 0 10px; color: var(--ab-fg); }
/* A linha de identidade do formulário: é recibo, não campo — por isso não tem a moldura
   de um .field, e fica com o mesmo peso de uma dica. */
.ident-linha {
  font: 400 13px/1.5 system-ui, sans-serif;
  color: var(--ab-muted);
  background: var(--ab-hover);
  border-radius: 8px;
  padding: 8px 10px;
  margin: 0 0 12px;
}
.ident-linha strong { color: var(--ab-fg); font-weight: 600; }

/* ---- Diálogo de esconder o launcher ---- */
.review.esconder h2 { font: 700 17px/1.3 system-ui, sans-serif; margin: 0 0 12px; color: var(--ab-fg); }
/* A dica é a ÚNICA porta de volta depois de "não": ganha superfície própria para não se
   perder como mais uma linha de texto do diálogo. */
.esc-tip {
  font: 400 13px/1.6 system-ui, sans-serif;
  color: var(--ab-muted);
  background: var(--ab-hover);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 18px;
}
.esc-tecla {
  display: inline-block;
  min-width: 20px;
  padding: 1px 6px;
  border: 1px solid var(--ab-input-border);
  border-bottom-width: 2px;  /* relevo de tecla: diz "aperte isto", não "leia isto" */
  border-radius: 5px;
  background: var(--ab-input-bg);
  color: var(--ab-fg);
  font: 700 12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace;
  text-align: center;
}

/* ---- Painel de suporte: menu de ações + Meus chamados (DEV-107) ---- */

/* Card do painel: mesma superfície da tela de sucesso (.review), mas alto e com
   rolagem interna — a thread de um chamado longo não deve empurrar a tela. */
/* Invólucro do painel: existe só para ancorar a badge de fechar FORA dele. O '.painel' tem
   'overflow: hidden' (é ele que recorta o corpo que rola nos cantos arredondados), e um filho
   em offset negativo seria cortado — daí a âncora ser uma camada acima. */
/* A LARGURA mora aqui, e não no '.painel'. Motivo: quem é filho do '.overlay' é esta caixa,
   então é ela que tem largura definida para uma percentagem morder — no '.painel', que é
   neto, o '100%' cai num pai shrink-to-fit e não limita nada (o painel ficava nos 560px
   inteiros dentro de uma tela de 375px). E '100%' em vez de '94vw' porque o overlay tem
   'padding: 24px' e viewport-unit não o desconta: em 375px o painel media 352,5px numa caixa
   de 327px e, como a largura era explícita, o flex item não encolhia — o overlay passava a
   rolar na HORIZONTAL. É o mesmo arranjo que o '.content' já usa. */
/* 60% DA TELA, com piso. A percentagem é da caixa de conteúdo do overlay, então já vem sem o
   'padding: 24px' dele — é por isso que a largura mora aqui e não no '.painel' (que é neto e
   resolveria a percentagem contra um pai shrink-to-fit, sem limitar nada).
   O piso é o que salva a tela pequena: 60% de 327px seriam 196px. 'min(100%, 560px)' dá a
   largura inteira no celular e mantém os 560px de antes no notebook, onde 60% ainda é menos. */
.painel-caixa {
  position: relative;
  margin: auto;
  width: 60%;
  min-width: min(100%, 560px);
}
/* Altura: só o teto da janela. O cap de 760px saiu — em tela alta ele deixava o painel
   pequeno com thread longa, que é justamente quando o espaço serve para algo. Segue
   'max-height', então conversa curta continua curta. 'dvh' pela barra de URL do Safari. */
.painel {
  width: 100%;
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  background: var(--ab-panel);
  border: 1px solid var(--ab-border);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,.4);
  overflow: hidden;
}
/* Fechar como BADGE FORA do painel, centrada no canto: a lista não tem título (o menu do
   launcher já disse onde a pessoa entrou), então não havia barra onde pousar o botão — e por
   fora ele não come espaço do conteúdo nem corre risco de cobrir a primeira linha. Vale nas
   DUAS telas (lista e detalhe): com o ✕ na barra só do detalhe, ele saltaria de lugar ao
   navegar. Translúcido e sem anel: ele é chrome, não conteúdo — o ✕ branco é o que se lê, e o
   disco só o separa o suficiente do que passa por baixo (metade card, metade fundo). */
.painel-fechar {
  position: absolute;
  top: -13px; right: -13px;
  z-index: 1;
  width: 28px; height: 28px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--ab-fechar);
  color: #fff;
  cursor: pointer;
}
.painel-fechar:hover { background: var(--ab-fechar-hover); }
.painel-fechar:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 2px; }
.painel-fechar svg { width: 13px; height: 13px; }
.painel-corpo { padding: 16px; overflow: auto; }
.painel-acao {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--ab-border);
  border-radius: 10px;
  background: transparent;
  color: var(--ab-fg);
  font: 600 14px/1.2 system-ui, sans-serif;
  text-align: left;
  cursor: pointer;
}
.painel-acao:hover { background: var(--ab-hover); }
.painel-acao svg { width: 18px; height: 18px; flex: 0 0 auto; color: var(--ab-accent); }
.painel-sec {
  display: flex; align-items: center; gap: 6px;
  font: 700 11px/1 system-ui, sans-serif;
  letter-spacing: .04em; text-transform: uppercase;
  color: var(--ab-muted);
  margin: 20px 0 8px;
}
.painel-sec svg { width: 14px; height: 14px; flex: 0 0 auto; }

/* Lista de chamados */
.ch-lista { display: flex; flex-direction: column; gap: 6px; }
.ch-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--ab-border);
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.ch-item:hover { background: var(--ab-hover); }
.ch-txt { flex: 1 1 auto; min-width: 0; }
.ch-titulo { display: block; font: 600 13px/1.35 system-ui, sans-serif; color: var(--ab-fg); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ch-meta { display: block; font: 400 12px/1.4 system-ui, sans-serif; color: var(--ab-muted); }
.ch-codigo { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
/* Marcador de resposta não vista: mesma bolha da badge do FAB, em escala de lista. */
.ch-novas {
  flex: 0 0 auto;
  min-width: 20px; height: 20px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0 6px;
  border-radius: 999px;
  /* MESMO vermelho da badge do launcher (e do contador no menu dele): é o mesmo aviso, visto
     de outra distância. Em azul de acento ele se confundia com os elementos clicáveis. */
  background: var(--ab-alerta); color: #fff;
  font: 700 11px/1 system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
}

/* Selo de situação. Só "resolvido" muda de cor (é a boa notícia); os demais são
   neutros de propósito — colorir "cancelado" de vermelho no widget do cliente
   transforma uma informação de processo em alarme. */
.ch-selo {
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--ab-border);
  background: var(--ab-hover);
  color: var(--ab-muted);
  font: 700 11px/1.3 system-ui, sans-serif;
  white-space: nowrap;
}
.ch-selo.ok { border-color: transparent; background: var(--ab-success); color: #fff; }

/* Seção "Encerrados" (DEV-183): cabeçalho de divisão, não um item da lista — sem caixa,
   sem borda, tipografia de rótulo. O que já acabou não pode competir com o que está vivo.
   A margem de cima abre o vão que separa os dois grupos (o gap da lista é de 6px). */
.ch-sec {
  display: flex; align-items: center; gap: 6px;
  width: 100%;
  margin: 10px 0 0;
  padding: 4px 2px;
  border: none;
  background: transparent;
  color: var(--ab-muted);
  font: 700 11px/1 system-ui, sans-serif;
  letter-spacing: .04em;
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;
}
.ch-sec:hover { color: var(--ab-fg); }
/* Faixa da seção "Compartilhados com você" (DEV-245). Mesma tipografia do .ch-sec, sem
   o chevron e sem o hover: aquela seção recolhe, esta não — o card do time é o que está
   pedindo atenção, e escondê-lo atrás de um clique desfaz o motivo de ele chegar aqui. */
.ch-faixa {
  margin: 10px 0 0;
  padding: 4px 2px;
  color: var(--ab-muted);
  font: 700 11px/1 system-ui, sans-serif;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.ch-sec:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 2px; border-radius: 6px; }
/* O mesmo chevron do voltar, girado: fechado aponta para a direita (há mais adiante),
   aberto aponta para baixo (o conteúdo está logo abaixo). Um glifo só, em dois estados —
   nada de dois ícones. O glifo nasce apontando para a ESQUERDA: 180° o vira para a direita
   e -90° o deita para baixo (com +90° ele apontaria para CIMA, que é o gesto de fechar). */
.ch-sec-chevron { display: grid; place-items: center; transform: rotate(180deg); }
.ch-sec-chevron.aberta { transform: rotate(-90deg); }
.ch-sec-chevron svg { width: 14px; height: 14px; display: block; }

/* Detalhe do chamado */
/* Cabeça do detalhe: voltar · (título + meta) · selo. O espaçamento até a thread vive aqui,
   e não na meta, porque nos estados de carregando e erro a meta não existe. */
.ch-cab { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 14px; }
.ch-pilha { flex: 1 1 auto; min-width: 0; }
.ch-cab h3 { font: 700 15px/1.3 system-ui, sans-serif; margin: 0; color: var(--ab-fg); }
.ch-sub { font: 400 12px/1.4 system-ui, sans-serif; color: var(--ab-muted); margin: 3px 0 0; }
/* Voltar: chevron SEM caixa. A caixa com borda do '.iconbtn' pesava mais que o título ao lado
   dela. A margem negativa compensa o vão do disco de 28px, pondo o GLIFO na mesma coluna do
   resto do corpo (o bloco de título fica recuado pela largura do controle, como em detalhe de
   app); o disco só aparece no hover, e é ele que mantém o alvo confortável. */
.ch-voltar {
  flex: 0 0 auto;
  width: 28px; height: 28px;
  margin: -4px 0 0 -6px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ab-muted);
  cursor: pointer;
}
.ch-voltar:hover { background: var(--ab-hover); color: var(--ab-fg); }
.ch-voltar:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 1px; }
.ch-voltar svg { width: 18px; height: 18px; }

/* ---- O report, na bolha que ABRE a conversa ----
   Ele é a primeira fala do cliente, então usa a bolha dele ('.ch-msg.minha') e só ajusta o
   que muda por estar FORA da '.ch-thread' — ele fica de fora porque é lá que o poll costura
   bolha por bolha pelo 'data-msg' (DEV-210). Sem o flex do container, o alinhamento à
   direita vem do 'margin-left:auto', e o encolher-com-o-conteúdo do 'width:fit-content':
   sem ele, um relato de três palavras ocuparia os 86% inteiros. */
.ch-report { width: fit-content; margin: 0 0 10px auto; }
/* Card compartilhado pelo time (DEV-245): a descrição é a nota DELES, então a bolha troca
   de lado — sem o flex do container, alinhar à esquerda é devolver a margem automática. */
.ch-report.do-time { margin: 0 auto 10px 0; }

.ch-anexos { display: flex; flex-wrap: wrap; gap: 8px; }
/* Miniatura: proporção fixa para a grade não dançar enquanto os blobs chegam em ordem
   diferente da que foram pedidos. */
.ch-anexo {
  position: relative;
  width: 176px; height: 112px;
  padding: 0;
  border: 1px solid var(--ab-border);
  border-radius: 8px;
  background: var(--ab-hover);
  overflow: hidden;
  cursor: zoom-in;
}
.ch-anexo[disabled] { cursor: default; }
.ch-anexo:hover:not([disabled]) { border-color: var(--ab-accent); }
.ch-anexo:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 2px; }
.ch-anexo img { display: block; width: 100%; height: 100%; object-fit: cover; }
.ch-anexo-carregando {
  display: grid; place-items: center;
  width: 100%; height: 100%;
  font: 400 11px/1.3 system-ui, sans-serif;
  color: var(--ab-muted);
  padding: 0 6px;
  text-align: center;
}
/* Anexo que não é imagem: chip com nome e tamanho. Não tem o que ampliar. */
.ch-anexo-arq {
  display: flex; align-items: center; gap: 8px;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--ab-border);
  border-radius: 8px;
  font: 400 12px/1.3 system-ui, sans-serif;
  color: var(--ab-fg);
}
.ch-anexo-nome { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ch-anexo-arq svg { width: 16px; height: 16px; flex: 0 0 auto; color: var(--ab-muted); }
.ch-anexo-tam { flex: 0 0 auto; color: var(--ab-muted); }

/* A lupa: acrescentada ao overlay, não é tela nova — fechar remove só ela e a conversa
   continua atrás, com os blobs já carregados. */
.ch-lupa {
  position: fixed;
  inset: 0;
  z-index: 1;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15,16,17,.88);
  cursor: zoom-out;
}
.ch-lupa img {
  max-width: 92vw;
  max-height: 88vh;
  border-radius: 8px;
  /* A print é de UI: escalar borrado atrapalha ler o texto dentro dela. */
  image-rendering: -webkit-optimize-contrast;
}
.ch-lupa-fechar { top: 16px; right: 16px; }

/* Thread: bolha do time à esquerda, do cliente à direita. */
.ch-thread { display: flex; flex-direction: column; gap: 10px; }
/* O teto em 'ch' entra junto com o painel mais largo: 86% de 560px dá linha longa demais
   para ler de corrido, e bolha de conversa é texto curto. Quem manda é o menor dos dois. */
.ch-msg { max-width: min(86%, 62ch); padding: 9px 12px; border-radius: 12px; background: var(--ab-hover); align-self: flex-start; }
.ch-msg.minha { align-self: flex-end; background: var(--ab-accent); }
.ch-msg.minha .ch-autor, .ch-msg.minha .ch-corpo { color: #fff; }
.ch-msg.minha .ch-autor { opacity: .85; }
.ch-autor { display: block; font: 700 11px/1.3 system-ui, sans-serif; color: var(--ab-muted); margin-bottom: 3px; }
.ch-corpo { font: 400 13px/1.45 system-ui, sans-serif; color: var(--ab-fg); white-space: pre-wrap; word-break: break-word; }
/* A imagem que a resposta trouxe, dentro da bolha (DEV-173). A miniatura da tela de
   detalhes tem 176px fixos; aqui ela ocupa a largura da bolha, porque é o desenho de que o
   texto fala e não um chip numa grade — e 'contain', pelo mesmo motivo do print único:
   recortar esconde justamente a parte que a resposta está explicando. A bolha já tem teto
   ('.ch-msg'), então "largura da bolha" não vira imagem gigante. */
.ch-msg-anexos { margin-top: 8px; }
/* Teto de altura junto com a proporção reservada: a proporção impede a bolha de dançar
   enquanto o blob chega, e o teto impede que uma imagem alta empurre a conversa inteira
   para fora da tela — na thread ela ilustra a resposta, e quem quer olhar de perto tem a
   lupa a um clique. Vale TAMBÉM para a print do report, que é retrato (a tela inteira de
   quem reportou) e sem o teto ocuparia o painel sozinha — era o que a regra
   '.ch-anexo:only-child' fazia, escrita para a tela de detalhes que saiu daqui. */
.ch-msg-anexos .ch-anexo { width: 100%; height: auto; aspect-ratio: 16 / 9; max-height: 200px; }
.ch-msg-anexos .ch-anexo img { object-fit: contain; }
/* Na bolha do cliente o fundo é o acento: a moldura neutra sumiria dentro dele. */
.ch-msg.minha .ch-anexo, .ch-msg.minha .ch-anexo-arq { border-color: rgba(255,255,255,.35); }
.ch-msg.minha .ch-anexo { background: rgba(255,255,255,.14); }
.ch-msg.minha .ch-anexo-arq, .ch-msg.minha .ch-anexo-carregando { color: #fff; }
.ch-msg.minha .ch-anexo-arq svg, .ch-msg.minha .ch-anexo-tam { color: rgba(255,255,255,.8); }
.ch-vazio { font: 400 13px/1.5 system-ui, sans-serif; color: var(--ab-muted); margin: 0; }

/* Composer da resposta do cliente. */
.ch-composer { margin-top: 14px; }
.ch-composer .ch-input {
  font: 400 13px/1.45 system-ui, sans-serif;
  color: var(--ab-fg);
  background: var(--ab-input-bg);
  border: 1px solid var(--ab-input-border);
  border-radius: 8px;
  padding: 8px 10px;
  width: 100%;
  resize: vertical;
  min-height: 56px;
}
.ch-composer .ch-input:focus { outline: 2px solid var(--ab-accent); outline-offset: -1px; border-color: var(--ab-accent); }
.ch-composer .ch-input:disabled { opacity: .6; }
/* Anexar à esquerda, enviar à direita: a ação secundária não pode disputar a borda com a
   principal, e é a distância entre as duas que diz qual é qual. */
.ch-composer-acoes { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: 8px; }
.ch-anexar { display: inline-flex; align-items: center; gap: 6px; }
.ch-anexar svg { width: 15px; height: 15px; flex: 0 0 auto; }
/* A miniatura do que vai junto (DEV-213), entre o campo e as ações — é onde ela responde
   "o que estou mandando?" sem empurrar o texto. Pequena de propósito: é confirmação, não
   visualização; quem quer olhar de perto já tem a imagem no aparelho. */
.ch-anexo-escolhido {
  position: relative;
  display: inline-block;
  margin-top: 8px;
  border: 1px solid var(--ab-border);
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
}
.ch-anexo-escolhido[hidden] { display: none; }
.ch-anexo-escolhido img { display: block; max-width: 160px; max-height: 90px; object-fit: cover; }
/* O ✕ fica SOBRE a miniatura, no canto: a faixa tem a largura da imagem, e um botão ao
   lado moveria a imagem de lugar conforme a proporção dela. */
.ch-anexo-tirar {
  position: absolute;
  top: 4px; right: 4px;
  width: 22px; height: 22px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--ab-fechar);
  color: #fff;
  cursor: pointer;
}
.ch-anexo-tirar:hover { background: var(--ab-fechar-hover); }
.ch-anexo-tirar svg { width: 12px; height: 12px; }
.ch-composer-off { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--ab-border); }
.ch-erro { font: 500 12px/1.4 system-ui, sans-serif; color: var(--ab-fg); background: var(--ab-hover); border-radius: 8px; padding: 8px 10px; margin: 0 0 12px; }

/* ---- Menu do launcher: as OUTRAS ações, dentro do painel ----
   Linhas em fluxo (não posicionadas): é isso que deixa o painel se dimensionar pela linha
   mais larga e dispensa qualquer medida em JS. A ação principal segue sendo o clique no
   launcher — o menu é o que estava escondido atrás dele.

   "Esconder" mora aqui porque antes era uma bolha com ✕ no canto: a bolha estourava o
   retângulo do launcher (ficava meio fora da tela junto à borda), disputava o canto com a
   badge de respostas novas e não dizia o que fazia. "Meus chamados" só aparece quando existe
   chamado para ver (local ou por sessão) — sem nada guardado seria um convite para uma lista
   vazia. "Esconder" está sempre lá, então o menu nunca é vazio. */

.fab-menu { display: none; flex-direction: column; gap: 2px; }
/* Com o launcher na metade de CIMA da tela o painel desce, senão sairia pela borda superior,
   onde não há como alcançá-lo. Descendo, a ordem das linhas inverte: a mais próxima da ação
   principal continua sendo a mais usada, e "esconder" segue na ponta mais distante. */
.fab.menu-abaixo .fab-menu { flex-direction: column-reverse; }
.fab:hover:not(.arrastando):not(.talo) .fab-menu,
.fab:focus-within:not(.arrastando):not(.talo) .fab-menu { display: flex; }

/* As linhas: superfície do painel, não o azul — o azul é da ação principal, e repeti-lo aqui
   faria três botões primários. Mesma escala da linha principal (14px, ícone 18px, gap 8px):
   medidas diferentes no mesmo painel parecem componentes diferentes. */
.fab-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--ab-fg);
  font: 500 14px/1.2 system-ui, sans-serif;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}
/* Espelha as linhas para o lado ANCORADO: o ícone da ação principal fica preso no canto (é o
   que evita o salto quando o rótulo abre), então os ícones do menu vão para a mesma coluna.
   Com metade das linhas de um lado e metade do outro, o painel não lê como um menu só. */
.fab.exp-left .fab-menu-item { flex-direction: row-reverse; text-align: right; }
/* O texto ocupa o meio: é ele, e não uma margem automática, que joga a contagem para a ponta
   oposta ao ícone — funciona igual nas duas direções. */
.fab-menu-txt { flex: 1 1 auto; }
.fab-menu-item:hover { background: var(--ab-hover); }
.fab-menu-item:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: -2px; }
.fab-menu-item svg { width: 18px; height: 18px; flex: 0 0 auto; color: var(--ab-muted); }
/* Contagem: o MESMO vermelho da badge em repouso e da lista — um só significado, uma só cor. */
.fab-menu-badge {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--ab-alerta);
  color: #fff;
  font: 700 11px/18px system-ui, sans-serif;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

/* Toque não tem hover: no celular quem abre o painel é o TOQUE no talo, e ele chega aqui
   como a classe '.aberto' (DEV-224). Repete o estado de hover porque ':hover' nunca vai
   casar naquele aparelho — é o preço de o painel ser um estado do próprio launcher.

   Antes isto era um '@media (hover: none)' incondicional, e era o bug: o painel inteiro
   ficava aberto na tela do celular o tempo todo, sem nunca passar pela bolinha. A classe é
   posta pelo JS SÓ quando a forma é 'cheio' (ver 'formaDoLauncher'), então ela nunca
   convive com '.talo' — e é por isso que estas regras não precisam do ':not(.talo)' que as
   de hover carregam. */
.fab.aberto:not(.arrastando) {
  gap: 2px;
  padding: 4px;
  border-radius: 16px;
  border-color: var(--ab-border);
  background: var(--ab-panel);
}
.fab.aberto:not(.arrastando) .fab-menu { display: flex; }
.fab.aberto:not(.arrastando) .fab-acao { padding: 12px 14px; border-radius: 12px; }
.fab.aberto:not(.arrastando) .fab-label { max-width: 220px; opacity: 1; }
.fab.aberto:not(.arrastando).exp-right .fab-label { margin-left: 8px; }
.fab.aberto:not(.arrastando).exp-left .fab-label { margin-right: 8px; }

/* Rodapé de identidade do painel: só o link, à esquerda — na mesma margem em que começam os
   títulos da lista, então ele entra na leitura em vez de virar um botão solto no canto. O
   filete fica (é estrutura, separa o rodapé da lista); o que saiu foi o texto em volta. */
.ch-sessao {
  margin: 16px 0 0;
  padding-top: 12px;
  /* 13px = borda (1) + padding (12) do '.ch-item': é o que põe o link na mesma coluna dos
     títulos, em vez de 13px à esquerda deles. */
  padding-left: 13px;
  border-top: 1px solid var(--ab-border);
  text-align: left;
  font: 400 12px/1.5 system-ui, sans-serif;
  color: var(--ab-muted);
}
.ch-link {
  padding: 0;
  border: none;
  background: none;
  color: var(--ab-accent);
  font: 600 12px/1.5 system-ui, sans-serif;
  cursor: pointer;
  text-decoration: underline;
}
.ch-link:disabled { opacity: .6; cursor: default; text-decoration: none; }

/* Badge de respostas novas no FAB — o único adorno do launcher em repouso, já que o ✕ virou
   linha de menu. No hover ela sai: a contagem reaparece na linha "Meus chamados", que diz
   ONDE está a novidade; mantê-la nos dois lugares parecia duas novidades diferentes. */
.fab-badge {
  position: absolute;
  top: -7px;
  /* grid + place-items centra sem depender de métrica de fonte herdada: com
     inline-flex + line-height o dígito descia meio pixel e o círculo saía torto.
     box-sizing content-box mantém 20px de CÍRCULO (a borda de 2px fica por fora, como
     anel); com border-box a área útil virava 16px e o dígito ficava apertado no canto. */
  box-sizing: content-box;
  width: 20px; height: 20px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 2px solid var(--ab-panel);
  border-radius: 999px;
  background: var(--ab-alerta); color: #fff;
  font: 700 11px/20px system-ui, sans-serif;
  /* Dígitos de largura fixa: sem isto "1" e "9" deslocam o centro do texto. */
  font-variant-numeric: tabular-nums;
  text-align: center;
  pointer-events: none;
}
/* Canto EXTERNO do ícone, o mesmo lado pelo qual o launcher está ancorado. */
.fab.exp-left .fab-badge { right: -7px; }
.fab.exp-right .fab-badge { left: -7px; }
.fab:hover .fab-badge,
.fab:focus-within .fab-badge,
.fab.aberto .fab-badge { display: none; }
/* No TALO ela cresce para DENTRO da tela: o talo mora colado na lateral, e os -7px que
   centram a badge no canto do círculo a jogariam metade para fora da viewport. Encostada na
   borda ancorada, ela cobre a ponta da pílula e sobra sobre os 30px de alvo transparente. */
.fab.talo.exp-left .fab-badge  { right: 0; left: auto; }
.fab.talo.exp-right .fab-badge { left: 0; right: auto; }
.fab.talo .fab-badge { top: -6px; }

/* ---- Balão de resposta nova (DEV-216) ----
   A badge diz que HÁ novidade; o balão diz QUAL é. Ele é IRMÃO do '.fab', nunca filho, e
   isso não é arrumação de DOM: como filho, o cursor que sai do botão em direção ao balão
   mantém o ':hover' do launcher — o painel abre, a regra abaixo esconde o balão, o cursor
   cai na página, o painel fecha, o balão volta sob o cursor. Um piscar infinito.

   Posicionado em JS a partir do rect do launcher (que é arrastável), mas ancorado pelas
   MESMAS bordas que ele usa (right/bottom quando está no canto de baixo à direita): assim
   os dois seguem juntos quando a janela muda de tamanho, sem listener de resize. */
.av-balao {
  position: fixed;
  z-index: 2147483001;
  width: 300px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px;
  border: 1px solid var(--ab-border);
  background: var(--ab-panel);
  color: var(--ab-fg);
  box-shadow: 0 10px 34px rgba(0,0,0,.22);
  text-align: left;
  cursor: pointer;
  pointer-events: auto;
  animation: ab-balao .22s cubic-bezier(.2,.7,.3,1) both;
}
.av-balao:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: 2px; }
/* Nasce do launcher: sobe 8px e cresce um triz. Distância curta com easing suave, como
   pede a régua de motion — nada de percorrer a tela. */
@keyframes ab-balao {
  from { opacity: 0; transform: translateY(8px) scale(.97); }
}
/* Movimento é opcional: sem ele o balão só aparece — e nada se perde, porque o fato
   continua na badge e na linha 'Meus chamados'. */
@media (prefers-reduced-motion: reduce) {
  /* Sem a corrida da barra: ela vira um traço parado, que ainda diz "tem algo aqui". */
  .gerando-barra::after { animation: none; width: 100%; }
  .av-balao { animation-name: ab-balao-surge; }
  @keyframes ab-balao-surge { from { opacity: 0; } }
}
/* O canto QUADRADO é o que fica junto do launcher — é dali que a ponta sai. */
.av-balao.av-acima.av-dir  { border-radius: 14px 14px 4px 14px; }
.av-balao.av-acima.av-esq  { border-radius: 14px 14px 14px 4px; }
.av-balao.av-abaixo.av-dir { border-radius: 14px 4px 14px 14px; }
.av-balao.av-abaixo.av-esq { border-radius: 4px 14px 14px 14px; }
/* A ponta: um losango com duas bordas, na mesma superfície do balão. Ela aponta para o
   CENTRO do círculo do launcher — 21px da borda ancorada (metade dos 42px do botão),
   menos a metade dos 11px do losango. */
.av-balao::after {
  content: "";
  position: absolute;
  width: 11px; height: 11px;
  background: var(--ab-panel);
  transform: rotate(45deg);
}
.av-balao.av-dir::after { right: 15px; }
.av-balao.av-esq::after { left: 15px; }
.av-balao.av-acima::after {
  bottom: -6px;
  border-right: 1px solid var(--ab-border);
  border-bottom: 1px solid var(--ab-border);
}
.av-balao.av-abaixo::after {
  top: -6px;
  border-left: 1px solid var(--ab-border);
  border-top: 1px solid var(--ab-border);
}
/* Some enquanto o painel do launcher está aberto (ou durante o arrasto): os dois ocupam o
   mesmo espaço, e ali a contagem já aparece na linha 'Meus chamados'. Mesma decisão da
   '.fab-badge', que também se apaga no hover. */
.fab:hover:not(.arrastando) ~ .av-balao,
.fab:focus-within:not(.arrastando) ~ .av-balao,
.fab.aberto:not(.arrastando) ~ .av-balao,
.fab.arrastando ~ .av-balao { display: none; }

/* Até 3 linhas e corta. O nome de quem respondeu é PREFIXO da frase (como em grupo de
   mensagem), e não uma linha própria: com três linhas no total, gastar uma com o nome
   custaria um terço da mensagem. */
.av-txt {
  margin: 0;
  padding-right: 18px; /* espaço do ✕, que flutua no canto */
  font: 400 12.5px/1.45 system-ui, sans-serif;
  color: var(--ab-muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere; /* URL ou palavra longa não estoura a caixa na página do cliente */
}
.av-quem { font-weight: 600; color: var(--ab-fg); }
/* Código e hora: quem tem mais de um chamado aberto precisa saber de QUAL deles é a fala. */
.av-meta { margin: 2px 0 0; font: 500 11px/1.35 system-ui, sans-serif; color: var(--ab-muted); }
.av-fechar {
  position: absolute;
  top: 6px; right: 6px;
  width: 22px; height: 22px;
  display: grid; place-items: center;
  padding: 0;
  border: 0; border-radius: 6px;
  background: transparent;
  color: var(--ab-muted);
  cursor: pointer;
}
.av-fechar:hover { background: var(--ab-hover); color: var(--ab-fg); }
.av-fechar:focus-visible { outline: 2px solid var(--ab-accent); outline-offset: -2px; }
.av-fechar svg { width: 13px; height: 13px; }

/* Toast — vive DENTRO de '.altbug' (e não solto no shadow root) justamente para herdar os
   tokens: como irmão, nenhum 'var(--ab-*)' resolvia e a cor tinha de ser literal — foi assim
   que ele ficou invisível no escuro, com o mesmo quase-preto do painel. */
.toast {
  position: fixed;
  left: 50%; bottom: 24px; transform: translateX(-50%);
  z-index: 2147483002;
  background: var(--ab-inverso); color: #fff;
  font: 500 13px/1.3 system-ui, sans-serif;
  padding: 10px 16px; border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,.3);
  pointer-events: none;
}
`;
